import { UserContext } from "../../../../context/userContext"
import { kioskApiGetParticipantsList } from "../../kioskApi"
import { useCallback, useContext, useEffect, useState } from "react"
import { LogError } from "../../../../helpers/logger"
import styles from "../../../../styles/kiosk/KioskParticipantManagement.module.css"
import { InputText } from "primereact/inputtext"
import CustomTable from "../CustomComponents/CustomTable"
import { LoaderMedium } from "../../../../components/Loaders"
import { Button } from "primereact/button"
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"
import LockModal from "./LockModal"
import SignInNoteModal from "./SignInNoteModal"
import { useLazyQuery } from "@apollo/client"
import { GET_PARTICIPANTS } from "../../queries"
import InactiveKioskLabel from "../InactiveKioskLabel"

const ParticipantManagement = ({ kiosk_organization_id, organization_id }) => {
  const [loading, setLoading] = useState(true)
  const [participants, setParticipants] = useState(null)
  const [settings, setSettings] = useState(null)
  const [participantsSettings, setParticipantsSettings] = useState(null)
  const [filteredParticipantsSettings, setFilteredParticipantsSettings] =
    useState([])
  const [showLockModal, setShowLockModal] = useState(false)
  const [showSignInNoteModal, setShowSignInNoteModal] = useState(false)
  const [selectedParticipant, setSelectedParticipant] = useState(null)
  const [refreshList, setRefreshList] = useState(true)
  const [filter, setFilter] = useState(null)

  const userCtx = useContext(UserContext)

  const [loadParticipants] = useLazyQuery(GET_PARTICIPANTS, {
    variables: {
      staff_id: -6,
      organization_id: organization_id,
    },
    fetchPolicy: "network-only",
  })

  const loadSettings = useCallback(async () => {
    try {
      if (kiosk_organization_id) {
        setLoading(true)
        const loadedParticipants = await loadParticipants({
          variables: {
            staff_id: -6,
            organization_id: organization_id,
          },
        })

        if (loadedParticipants?.data?.search) {
          setParticipants(loadedParticipants?.data?.search)

          const result = await kioskApiGetParticipantsList(
            userCtx.tokenKiosk,
            kiosk_organization_id
          )
          setSettings(result?.settings || [])
          setRefreshList(false)
          setLoading(false)
        } else {
          LogError(
            "Unable to retrieve the organization participants",
            loadedParticipants
          )
          userCtx.setNotificationError("Unable to load participants")
          setLoading(false)
        }
      } else {
        setParticipants([])
        setSettings([])
        setLoading(false)
      }
    } catch (error) {
      LogError("Unable to load KIOSK Participant Settings", error)
      userCtx.setNotificationError("Unable to load Kiosk Participants settings")
      setSettings([])
      setRefreshList(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosk_organization_id, organization_id])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  useEffect(() => {
    if (refreshList) {
      loadSettings()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshList])

  useEffect(() => {
    if (settings && participants) {
      setLoading(true)
      const participantSettings = participants.map((participant) => {
        const participantSettings = settings.find(
          (setting) => setting?.participantId === participant.participant_id
        )
        return {
          profile: {
            firstName: participant.participant_name_first,
            lastName: participant?.participant_name_last,
            status: participant?.participant_membership_status,
            participantId: participant?.participant_id,
            participantMembershipId: participant?.participant_membership_id,
          },
          settings: participantSettings,
        }
      })
      setParticipantsSettings(participantSettings)

      if (filter) {
        filterParticipants(filter)
      } else {
        setFilteredParticipantsSettings(participantSettings)
      }
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, participants])

  const mapParticipants = () => {
    return filteredParticipantsSettings?.map((participant) => {
      return {
        firstName: participant.profile?.firstName,
        lastName: participant.profile?.lastName,
        status: participant.settings?.locked,
        showSignInNote: participant.settings?.showSignInNote,
        signInNote: participant.settings?.signInNote,
        participantId: participant.profile?.participantId,
        participantMembershipId: participant.profile?.participantMembershipId,
        lockExpiry: participant.settings?.lockExpiry,
        lockMessage: participant.settings?.lockMessage,
      }
    })
  }
  const headers = () => {
    return [
      { value: "firstName", label: "First Name" },
      { value: "lastName", label: "Last Name" },
      {
        value: "status",
        label: "Status",
        dataTemplate: statusTemplate,
        className: styles.statusColumn,
      },
      {
        value: "signInNote",
        label: "Sign-In Note",
        dataTemplate: signInNoteTemplate,
        className: styles.signInNoteColumn,
      },
      {
        value: "actions",
        label: undefined,
        dataTemplate: actionsTemplate,
        className: styles.actionColumn,
      },
    ]
  }
  const statusTemplate = (data) => {
    if (!data.status) {
      return (
        <div className={`${styles.status} ${styles.statusUnlocked}`}>
          Unlocked
        </div>
      )
    }
    return (
      <div className={`${styles.status} ${styles.statusLocked}`}>Locked</div>
    )
  }
  const signInNoteTemplate = (data) => {
    if (!data.showSignInNote || !data.signInNote) {
      return (
        <div className={`${styles.signInNote} ${styles.signInNoteInactive}`}>
          Not set
        </div>
      )
    }
    return (
      <div className={`${styles.signInNote} ${styles.signInNoteActive}`}>
        Active
      </div>
    )
  }
  const actionsTemplate = (data) => {
    return (
      <div className={styles.actionsContainer}>
        <Button
          icon="pi pi-unlock"
          className={kioskStyles.roundedButton}
          onClick={() => {
            setSelectedParticipant(data)
            setShowLockModal(true)
          }}
          tooltip="Lock / Unlock"
          tooltipOptions={{ position: "bottom", className: "custom-tooltip" }}
        />
        <Button
          icon="pi pi-comment"
          className={kioskStyles.roundedButton}
          onClick={() => {
            setSelectedParticipant(data)
            setShowSignInNoteModal(true)
          }}
          tooltip="Sign-in note"
          tooltipOptions={{ position: "bottom", className: "custom-tooltip" }}
        />
      </div>
    )
  }
  const filterParticipants = (key) => {
    const participantsFiltered = participantsSettings.filter((participant) => {
      const matchesFirstName = participant.profile.firstName
        .toLowerCase()
        .includes(key.toLowerCase())
      const matchesLastName = participant.profile.lastName
        .toLowerCase()
        .includes(key.toLowerCase())
      const fullName = `${participant.profile.firstName} ${participant.profile.lastName}`
      const matchesFullName = fullName
        .toLocaleLowerCase()
        .includes(key.toLocaleLowerCase())
      return matchesFirstName || matchesLastName || matchesFullName
    })
    setFilteredParticipantsSettings(participantsFiltered)
  }
  return (
    <>
      {loading ? (
        <LoaderMedium />
      ) : !kiosk_organization_id ? (
        <InactiveKioskLabel />
      ) : (
        <>
          <div className={styles.participantSearchBoxContainer}>
            <div className={`${"p-inputgroup"} ${styles.searchBox}`}>
              <InputText
                placeholder="Search participant by name"
                value={filter}
                className={styles.searchInput}
                onChange={(e) => {
                  setFilter(e.target.value)
                  filterParticipants(e.target.value)
                }}
              />
              <span className={`${"p-inputgroup-addon"} ${styles.searchIcon}`}>
                <i className="pi pi-search" />
              </span>
            </div>
            <Button
              icon="pi pi-times"
              className={kioskStyles.roundedButton}
              onClick={() => {
                setFilter("")
                setFilteredParticipantsSettings(participantsSettings)
              }}
              tooltip="Clear search"
              tooltipOptions={{
                position: "bottom",
                className: "custom-tooltip",
              }}
            />
          </div>
          <CustomTable
            headers={headers()}
            collection={mapParticipants()}
            showPaginator={true}
          />
        </>
      )}
      <LockModal
        show={showLockModal}
        setShow={setShowLockModal}
        selectedParticipant={selectedParticipant}
        setSelectedParticipant={setSelectedParticipant}
        kiosk_organization_id={kiosk_organization_id}
        refreshList={setRefreshList}
      />
      <SignInNoteModal
        show={showSignInNoteModal}
        setShow={setShowSignInNoteModal}
        selectedParticipant={selectedParticipant}
        setSelectedParticipant={setSelectedParticipant}
        kiosk_organization_id={kiosk_organization_id}
        refreshList={setRefreshList}
      />
    </>
  )
}

export default ParticipantManagement
